import { useMemo, useState } from "react";

import {
  ArrowLeft,
  Check,
  Link2,
  Link2Off,
  Loader2,
  Plus,
  RotateCw,
  Search,
  TriangleAlert,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Skeleton } from "../../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { cn } from "../../../lib/utils";
import { getIcon } from "../../../utils/getIcon";
import { INTEGRATION_SCOPES } from "../../constants";
import { useCrawlerPreferenceTriggerRefetch } from "../../hooks/useCrawlerPreferenceTriggerRefetch";
import { useDelinkIntegration } from "../../hooks/useDelinkIntegration";
import { useDeploymentWorkspaces } from "../../hooks/useDeploymentWorkspaces";
import { useFetchAllIntegrations } from "../../hooks/useFetchAllIntegrations";
import { useFetchCrawlerPreference } from "../../hooks/useFetchCrawlerPreference";
import {
  deriveCrawlerPreferenceStatus,
  getCrawlerPreferenceNoun,
  getCrawlerPreferenceRefreshTooltip,
  isCrawlerPreferenceReady,
} from "../../utils";
import { IntegrationModal } from "../IntegrationModal";
import { ErrorState } from "../shared/ErrorState";

import { ConfigureWorkspaceDialog } from "./ConfigureWorkspaceDialog";

import type { DeploymentWorkspace } from "../../hooks/useDeploymentWorkspaces";
import type { ConnectedIntegrationDetail } from "../../types/integrations";
import type { ModalStep } from "../../types/modal";
import type { CrawlerPreferenceStatus } from "../../utils";

const VISIBLE_FOLDER_CHIPS = 3;

const SelectedFolders = ({ folders }: { folders: string[] }) => {
  const hidden = folders.slice(VISIBLE_FOLDER_CHIPS);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {folders.slice(0, VISIBLE_FOLDER_CHIPS).map((folder) => (
        <Badge key={folder} variant="secondary" className="font-normal">
          {folder}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="cursor-default font-normal">
              +{hidden.length} more
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {hidden.join(", ")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

const WorkspaceRow = ({
  workspace,
  preferenceNoun,
  disabledReason,
  onConfigure,
}: {
  workspace: DeploymentWorkspace;
  preferenceNoun: string;
  disabledReason?: string;
  onConfigure: (workspace: DeploymentWorkspace) => void;
}) => {
  const { organization, integration, selectedFolders } = workspace;

  const action = (
    <Button
      variant={integration ? "outline" : "default"}
      size="sm"
      disabled={!!disabledReason}
      onClick={() => onConfigure(workspace)}
    >
      {integration ? "Edit" : "Configure"}
    </Button>
  );

  return (
    <TableRow data-testid={`workspace-row-${organization.organization_uuid}`}>
      <TableCell className="align-middle">
        <p className="font-medium">{organization.name}</p>
        <p className="text-xs text-muted-foreground">{organization.domain}</p>
      </TableCell>
      <TableCell>
        {!integration ? (
          <span className="text-sm text-muted-foreground">Not configured</span>
        ) : selectedFolders.length > 0 ? (
          <SelectedFolders folders={selectedFolders} />
        ) : (
          <span className="text-sm text-muted-foreground">
            No {preferenceNoun} selected
          </span>
        )}
      </TableCell>
      <TableCell className="text-right align-middle">
        {disabledReason ? (
          /* A disabled button with no explanation is worse than no button. */
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{action}</span>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        ) : (
          action
        )}
      </TableCell>
    </TableRow>
  );
};

const STATUS_TONE: Record<CrawlerPreferenceStatus, string> = {
  expired: "bg-destructive/10 text-destructive",
  error: "bg-destructive/10 text-destructive",
  fetching:
    "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  loading: "text-muted-foreground",
  ready: "text-muted-foreground",
};

const AccountStatusStrip = ({
  status,
  message,
}: {
  status: CrawlerPreferenceStatus;
  message: string;
}) => (
  <div
    className={cn(
      "flex items-center gap-2 border-t px-4 py-2.5 text-xs",
      STATUS_TONE[status],
    )}
  >
    {status === "expired" || status === "error" ? (
      <TriangleAlert className="h-3.5 w-3.5 flex-shrink-0" />
    ) : status === "ready" ? (
      <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />
    ) : (
      <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
    )}
    <span className="truncate">{message}</span>
  </div>
);

const AccountSelector = ({
  accounts,
  selectedAccount,
  onSelect,
}: {
  accounts: ConnectedIntegrationDetail[];
  selectedAccount: ConnectedIntegrationDetail;
  onSelect: (verifiableId: string) => void;
}) => {
  if (accounts.length === 1) {
    return (
      <p className="text-sm font-semibold">{selectedAccount.verifiable_id}</p>
    );
  }

  return (
    <Select value={selectedAccount.verifiable_id} onValueChange={onSelect}>
      <SelectTrigger className="h-8 w-fit gap-2 border-none px-0 text-sm font-semibold shadow-none focus:ring-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {accounts.map((account) => (
          <SelectItem key={account.verifiable_id} value={account.verifiable_id}>
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  account.is_valid_token ? "bg-green-500" : "bg-destructive",
                )}
              />
              {account.verifiable_id}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const DeploymentSourceWorkspaces = () => {
  const { source = "" } = useParams();
  const navigate = useNavigate();
  const [activeWorkspace, setActiveWorkspace] =
    useState<DeploymentWorkspace | null>(null);
  /*
  Which account is in view is deliberately kept out of the URL, so a refresh falls back to
  the first connected account.
  */
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  /*
  Set only for a click-initiated connect. It must stay unset on the OAuth return trip, so
  the modal resolves that to the finish step instead of showing terms again.
  */
  const [connectStep, setConnectStep] = useState<ModalStep>();
  const [workspaceQuery, setWorkspaceQuery] = useState("");

  const { accounts, selectedAccount, workspaces, isLoading, isError, refetch } =
    useDeploymentWorkspaces(source, selectedAccountId);

  // Already cached by the list page; used for the display name and the callback modal.
  const { data: integrations, isLoading: isSourcesLoading } =
    useFetchAllIntegrations(INTEGRATION_SCOPES.DEPLOYMENT);
  const integration = integrations?.find((item) => item.label === source);
  const sourceLabel = integration?.value ?? source;

  /*
  Which sources may be connected for the whole deployment is the API's call, so an
  unknown or unsupported :source is whatever the listing did not return.
  */
  const isUnsupportedSource =
    !isSourcesLoading && !!integrations && !integration;

  /*
  The folder tree belongs to the account, not to a workspace, so it is fetched here rather
  than per dialog. Every workspace selects from this one tree, and until it arrives there
  is nothing to configure — the hook polls a WAIT response, so the table unlocks on its own
  the moment it lands.
  */
  const {
    data: parentPreference,
    isLoading: isTreeLoading,
    error: treeFetchError,
  } = useFetchCrawlerPreference(source, selectedAccount?.verifiable_id ?? "", {
    integrationScope: INTEGRATION_SCOPES.DEPLOYMENT,
    enabled: !!selectedAccount,
  });

  const triggerRefetch = useCrawlerPreferenceTriggerRefetch();

  const handleRefreshFolders = () => {
    if (!selectedAccount) return;

    triggerRefetch.mutate({
      source,
      verifiableId: selectedAccount.verifiable_id,
      integrationScope: INTEGRATION_SCOPES.DEPLOYMENT,
    });
  };

  /*
  Delink the selected account by verifiable_id rather than through useIntegrationActions,
  which resolves the account positionally — wrong once a source holds several.
  */
  const { mutate: delinkIntegration, isPending: isDelinking } =
    useDelinkIntegration({ onSuccess: () => setSelectedAccountId(undefined) });

  const handleDelink = () => {
    if (!selectedAccount) return;

    delinkIntegration({
      verifiableId: selectedAccount.verifiable_id,
      source,
      integrationScope: INTEGRATION_SCOPES.DEPLOYMENT,
    });
  };

  const needsReconnect = !!selectedAccount && !selectedAccount.is_valid_token;

  const preferenceNoun = getCrawlerPreferenceNoun(source);

  const { status: treeStatus, errorMessage: treeErrorMessage } =
    deriveCrawlerPreferenceStatus({
      data: parentPreference,
      isLoading: isTreeLoading,
      isError: !!treeFetchError,
      isValidToken: !needsReconnect,
      isTriggering: triggerRefetch.isPending,
    });

  const TREE_STATUS_COPY: Record<CrawlerPreferenceStatus, string> = {
    expired: `Access expired — reconnect to keep ${preferenceNoun} in sync`,
    error:
      treeErrorMessage ||
      `Couldn't read ${preferenceNoun} from this account — try Refresh`,
    loading: `Loading ${sourceLabel} ${preferenceNoun}`,
    fetching: `${sourceLabel} is still listing ${preferenceNoun} — this can take a few minutes, come back shortly`,
    ready: `${sourceLabel} ${preferenceNoun} fetched — assign them to workspaces below`,
  };

  const configureDisabledReason = needsReconnect
    ? "Reconnect this account first"
    : !isCrawlerPreferenceReady(parentPreference) || triggerRefetch.isPending
      ? `Still fetching ${preferenceNoun}`
      : undefined;

  const filteredWorkspaces = useMemo(() => {
    const query = workspaceQuery.trim().toLowerCase();
    if (!query) return workspaces;

    return workspaces.filter(
      ({ organization }) =>
        organization.name.toLowerCase().includes(query) ||
        organization.domain?.toLowerCase().includes(query),
    );
  }, [workspaces, workspaceQuery]);

  if (isUnsupportedSource) {
    return (
      <div className="mx-auto w-full max-w-5xl px-8 py-10">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-1.5 w-fit gap-1.5 pl-1.5 text-muted-foreground hover:text-foreground"
          onClick={() => navigate("..")}
        >
          <ArrowLeft className="h-4 w-4" />
          Deployment Integrations
        </Button>
        <ErrorState
          title="Source not available"
          message={`${source} cannot be connected for the whole deployment.`}
          className="rounded-lg border bg-card p-6"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-8 py-10">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4 -ml-1.5 w-fit gap-1.5 pl-1.5 text-muted-foreground hover:text-foreground"
        onClick={() => navigate("..")}
      >
        <ArrowLeft className="h-4 w-4" />
        Deployment Integrations
      </Button>

      {isLoading && (
        <>
          <div className="mb-6 rounded-xl border bg-card">
            <div className="flex items-center gap-4 p-4">
              <Skeleton className="h-10 w-10 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-3 w-72" />
              </div>
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
            <div className="border-t px-4 py-2.5">
              <Skeleton className="h-3.5 w-72" />
            </div>
          </div>
          <div className="mb-3 flex items-center justify-between gap-4">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-10 w-64" />
          </div>
          <div className="overflow-hidden rounded-xl border bg-card">
            {Array.from({ length: 4 }, (_, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-4 border-b p-4 last:border-b-0"
              >
                <div className="w-[240px] space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        </>
      )}

      {isError && (
        <>
          <ErrorState
            title="Failed to load accounts"
            className="mb-4 rounded-lg border bg-card p-6"
          />
          <Button variant="outline" onClick={refetch}>
            Retry
          </Button>
        </>
      )}

      {!isLoading && !isError && !selectedAccount && (
        <ErrorState
          title={`No ${sourceLabel} account connected`}
          message="Connect an account from the deployment integrations list to start assigning it to workspaces."
          className="rounded-lg border bg-card p-6"
        />
      )}

      {!isLoading && !isError && selectedAccount && (
        <>
          <div className="mb-6 rounded-xl border bg-card">
            <div className="flex items-center gap-4 p-4">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-muted">
                {getIcon(source)}
              </div>
              <div className="min-w-0 flex-1">
                <AccountSelector
                  accounts={accounts}
                  selectedAccount={selectedAccount}
                  onSelect={setSelectedAccountId}
                />
                <p className="text-xs text-muted-foreground">
                  {sourceLabel} · {accounts.length}{" "}
                  {accounts.length === 1 ? "account" : "accounts"} connected for
                  the whole deployment
                </p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {needsReconnect ? (
                  <Button
                    size="sm"
                    onClick={() => setConnectStep("authenticate")}
                  >
                    <Link2 className="mr-1 h-4 w-4" />
                    Reconnect
                  </Button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={triggerRefetch.isPending}
                          onClick={handleRefreshFolders}
                        >
                          <RotateCw className="mr-1 h-4 w-4" />
                          Refresh
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {getCrawlerPreferenceRefreshTooltip(source)}
                    </TooltipContent>
                  </Tooltip>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConnectStep("authenticate")}
                  data-testid="deployment-add-account-button"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add account
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={isDelinking}
                  onClick={handleDelink}
                >
                  <Link2Off className="mr-1 h-4 w-4" />
                  {isDelinking ? "Delinking..." : "Delink"}
                </Button>
              </div>
            </div>

            {/* Always rendered, so the table below never shifts. */}
            <AccountStatusStrip
              status={treeStatus}
              message={TREE_STATUS_COPY[treeStatus]}
            />
          </div>

          <div className="mb-3 flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {workspaceQuery.trim()
                ? `${filteredWorkspaces.length} of ${workspaces.length} workspaces`
                : `${workspaces.length} ${
                    workspaces.length === 1 ? "workspace" : "workspaces"
                  }`}
            </p>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search workspaces"
                value={workspaceQuery}
                onChange={(event) => setWorkspaceQuery(event.target.value)}
                aria-label="Search workspaces"
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border bg-card">
            {/* Capped so a long workspace list scrolls inside the page rather than
            stretching it; the header sticks so columns stay readable while scrolling. */}
            <div className="max-h-[clamp(420px,68vh,760px)] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead className="w-[260px]">Workspace</TableHead>
                    <TableHead>
                      Connected {sourceLabel} {preferenceNoun}
                    </TableHead>
                    <TableHead className="w-[140px] text-right">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkspaces.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="py-10 text-center text-sm text-muted-foreground"
                      >
                        No workspaces match &ldquo;{workspaceQuery.trim()}
                        &rdquo;
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredWorkspaces.map((workspace) => (
                      <WorkspaceRow
                        key={workspace.organization.organization_uuid}
                        workspace={workspace}
                        preferenceNoun={preferenceNoun}
                        disabledReason={configureDisabledReason}
                        onConfigure={setActiveWorkspace}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {activeWorkspace && isCrawlerPreferenceReady(parentPreference) && (
            <ConfigureWorkspaceDialog
              source={source}
              sourceLabel={sourceLabel}
              parentVerifiableId={selectedAccount.verifiable_id}
              parentPreference={parentPreference}
              workspace={activeWorkspace}
              onClose={() => setActiveWorkspace(null)}
            />
          )}
        </>
      )}

      {/*
      The same flow the list page uses. startStep pins it to terms acceptance, which the
      connection status would otherwise resolve to the manage step. It also opens itself
      off the ?connect= param to show the finish step after the OAuth return.
      */}
      {integration && (
        <IntegrationModal
          isOpen={!!connectStep}
          onClose={() => setConnectStep(undefined)}
          integration={integration}
          integrationScope={INTEGRATION_SCOPES.DEPLOYMENT}
          startStep={connectStep}
        />
      )}
    </div>
  );
};
